import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Boxes, Package } from 'lucide-react';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { allocationsApi, stockApi } from '@/features/stock/api';
import { factsApi } from '@/features/insights/factsApi';
import { mergeLedgers, unaccountedOf } from '@/features/insights/engine';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { useCurrentBrand } from '@/lib/branding/useCurrentBrand';
import { useAuth } from '@/lib/auth/useAuth';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import type { XkEvent, XkEventStockStation, XkStock } from '@/lib/api/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { cn } from '@/lib/cn';

/**
 * CH-11 — Brand inventory es My Merch visto por marca, con los estados del
 * libro de premios. No hay un pool aparte que haya que «promocionar»: la
 * pantalla anterior leía `co_brand_inventory`, una tabla que nadie escribía,
 * y por eso salía vacía con merch de sobra en My Merch.
 *
 * Por cada ítem de la marca: total dado de alta, lo que queda en almacén
 * (`quantityRemaining − Σ available`), lo asignado y aún en juego, y lo que
 * el ledger de todos los eventos de la marca dice que pasó con lo que salió:
 * entregado, pendiente de recoger, devuelto al cerrar, caducado o anulado, y
 * sin destino. Mismos hechos y mismo motor que el informe de cada evento.
 */
type State =
  | 'unassigned'
  | 'allocated'
  | 'distributed'
  | 'pending'
  | 'returned'
  | 'lapsed'
  | 'unaccounted';

const STATES: State[] = ['unassigned', 'allocated', 'distributed', 'pending', 'returned', 'lapsed', 'unaccounted'];

interface Row {
  stock: XkStock;
  total: number;
  unassigned: number;
  allocated: number;
  distributed: number;
  pending: number;
  returned: number;
  lapsed: number;
  unaccounted: number;
}

export function BrandInventoryListPage() {
  const t = useT();
  useFavoritable(t('brandInventory.title'));
  const { user } = useAuth();
  const selectedBrandId = useSelectedBrandId();
  const brandId = selectedBrandId ?? user?.companyId ?? null;
  const { brands } = useCurrentBrand();
  const brandName = brands.find((b) => b.id === brandId)?.name ?? null;
  const [state, setState] = useState<State | null>(null);

  const stocksQ = useQuery({
    queryKey: queryKeys.stock.byBrand(brandId ?? 0),
    queryFn: () => stockApi.list({ companyId: brandId }, { perPage: 500 }),
    enabled: !!brandId,
  });
  const allocsQ = useQuery({
    queryKey: queryKeys.stockAllocations.byBrand(brandId ?? 0),
    queryFn: () => allocationsApi.list({ companyId: brandId }, { perPage: 500 }),
    enabled: !!brandId,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () => api.list<XkEvent>('/xk-event', { filter: { companyId: brandId }, perPage: 500 }),
    enabled: !!brandId,
  });
  const eventIds = useMemo(
    () => (eventsQ.data?.items ?? []).map((e) => e.id).sort((a, b) => a - b),
    [eventsQ.data],
  );
  const factsQ = useQuery({
    queryKey: queryKeys.insights.facts(eventIds),
    queryFn: () => factsApi.forEvents(eventIds),
    enabled: eventIds.length > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo<Row[]>(() => {
    const stocks = stocksQ.data?.items ?? [];
    const inPlayByStock = new Map<number, number>();
    for (const a of (allocsQ.data?.items ?? []) as XkEventStockStation[]) {
      if ((a.status as string) === 'Deleted') continue;
      inPlayByStock.set(a.stockId, (inPlayByStock.get(a.stockId) ?? 0) + (a.available ?? 0));
    }
    const ledger = new Map(mergeLedgers(factsQ.data?.events ?? []).map((r) => [r.stockId, r]));
    return stocks.map((stock) => {
      const l = ledger.get(stock.id);
      const allocated = inPlayByStock.get(stock.id) ?? 0;
      return {
        stock,
        total: stock.initialQuantity ?? 0,
        unassigned: Math.max(0, (stock.quantityRemaining ?? 0) - allocated),
        allocated,
        distributed: l?.delivered ?? 0,
        pending: l?.pending ?? 0,
        returned: l?.returned ?? 0,
        lapsed: (l?.expired ?? 0) + (l?.voided ?? 0),
        unaccounted: l ? unaccountedOf(l) : 0,
      };
    });
  }, [stocksQ.data, allocsQ.data, factsQ.data]);

  const counts = useMemo(() => {
    const out = {} as Record<State, number>;
    for (const s of STATES) out[s] = rows.filter((r) => r[s] > 0).length;
    return out;
  }, [rows]);
  const shown = state ? rows.filter((r) => r[state] > 0) : rows;

  const loading = stocksQ.isLoading || allocsQ.isLoading || eventsQ.isLoading;
  const ledgerLoading = eventIds.length > 0 && factsQ.isLoading;

  return (
    <div className="page space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
            {t('brandInventory.title')}
            <HelpHint section="brand-inventory" />
          </h1>
          {/* La marca, en la cabecera y no sólo en la miga: la pantalla es de
              una marca y el alcance tiene que verse. */}
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {brandName ? (
              <>
                <span className="font-semibold text-[var(--color-fg-primary)]">{brandName}</span>
                {' · '}
              </>
            ) : null}
            {t('brandInventory.basis', { n: eventIds.length })}
          </p>
        </div>
        <Link to="/stock">
          <Button variant="secondary">{t('brandInventory.goToMerch')}</Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Boxes className="h-4 w-4" />
            {t('brandInventory.filterByState')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <FilterChip label={t('brandInventory.allStates')} count={rows.length} active={state === null} onClick={() => setState(null)} />
            {STATES.map((s) => (
              <FilterChip
                key={s}
                label={t(`brandInventory.state.${s}`)}
                count={counts[s]}
                active={state === s}
                onClick={() => setState(state === s ? null : s)}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Package className="h-8 w-8" />}
              title={t('brandInventory.emptyNoMerch', { brand: brandName ?? '' })}
              description={t('brandInventory.emptyNoMerchHint')}
              action={
                <Link to="/stock">
                  <Button>{t('brandInventory.goToMerch')}</Button>
                </Link>
              }
            />
          ) : shown.length === 0 ? (
            <EmptyState
              icon={<Package className="h-8 w-8" />}
              title={t('brandInventory.emptyFilter', { state: state ? t(`brandInventory.state.${state}`) : '' })}
              description={t('brandInventory.emptyFilterHint', { n: rows.length })}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead className="border-b border-[var(--color-border-subtle)] text-left text-xs uppercase text-[var(--color-fg-muted)]">
                  <tr>
                    <th className="px-4 py-3">{t('brandInventory.col.name')}</th>
                    <th className="px-3 py-3 text-right">{t('brandInventory.col.total')}</th>
                    {STATES.map((s) => (
                      <th key={s} className={cn('px-3 py-3 text-right', state === s && 'text-[var(--color-accent-ink)]')}>
                        {t(`brandInventory.state.${s}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.stock.id} className="border-b border-[var(--color-border-subtle)] hover:bg-[var(--color-xk-surface)]">
                      <td className="px-4 py-3 font-medium text-[var(--color-fg-primary)]">
                        <div className="flex items-center gap-3">
                          {r.stock.image || r.stock.logo ? (
                            <img
                              src={resolveImageUrl((r.stock.image || r.stock.logo) ?? undefined)}
                              alt=""
                              className="h-10 w-10 rounded object-contain"
                            />
                          ) : (
                            <div className="grid h-10 w-10 place-items-center rounded bg-[var(--color-xk-surface-2)] text-[var(--color-fg-muted)]">
                              <Package className="h-4 w-4" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="truncate">{r.stock.name}</div>
                            <div className="text-[11px] text-[var(--color-fg-subtle)]">
                              {t('brandInventory.rarity', { n: r.stock.rating ?? 3 })}
                            </div>
                          </div>
                        </div>
                      </td>
                      <Num v={r.total} />
                      <Num v={r.unassigned} strong={state === 'unassigned'} />
                      <Num v={r.allocated} strong={state === 'allocated'} />
                      {ledgerLoading ? (
                        <td colSpan={5} className="px-3 py-3 text-right text-xs text-[var(--color-fg-subtle)]">{t('common.loading')}</td>
                      ) : (
                        <>
                          <Num v={r.distributed} strong={state === 'distributed'} />
                          <Num v={r.pending} strong={state === 'pending'} />
                          <Num v={r.returned} strong={state === 'returned'} />
                          <Num v={r.lapsed} strong={state === 'lapsed'} />
                          <Num v={r.unaccounted} strong={state === 'unaccounted'} tone={r.unaccounted > 0 ? 'warn' : undefined} />
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Num({ v, strong, tone }: { v: number; strong?: boolean; tone?: 'warn' }) {
  return (
    <td
      className={cn(
        'px-3 py-3 text-right tabular-nums',
        v === 0 && 'text-[var(--color-fg-subtle)]',
        strong && 'font-bold text-[var(--color-accent-ink)]',
        tone === 'warn' && v > 0 && 'text-[var(--color-warn)]',
      )}
    >
      {v}
    </td>
  );
}

function FilterChip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-15)] text-[var(--color-accent-ink)]'
          : 'border-[var(--color-border-subtle)] text-[var(--color-fg-muted)] hover:bg-[var(--color-xk-surface)]',
      )}
    >
      {label}
      <span className={cn('rounded-full px-1.5 text-[10px] font-bold tabular-nums', active ? 'bg-[var(--color-accent-ink)] text-[var(--color-fg-on-accent)]' : 'bg-[var(--color-xk-surface-2)]')}>
        {count}
      </span>
    </button>
  );
}
